import { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  RotateCcw,
  Zap,
  Landmark,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { DashboardLayout } from '../components/DashboardLayout';
import { schedulerAPI, bankConsentsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

const stateConfig = {
  closed: {
    label: 'Working',
    icon: ShieldCheck,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950/30',
    border: 'border-green-200 dark:border-green-800',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  },
  open: {
    label: 'Failing, paused',
    icon: ShieldAlert,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
  half_open: {
    label: 'Retrying',
    icon: ShieldQuestion,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  },
};

// A connection counts as quiet when its last good call is older than this.
const QUIET_HOURS = 48;

// The breaker keeps its last error forever, even after later calls succeed.
// Only show it as current when it is newer than the last success (Fan,
// 2026-10-22: every card said "Timeout" from a 24 Aug outage yet "Healthy").
export function circuitView(circuit, now) {
  const t = (iso) => (iso ? new Date(iso).getTime() : 0);
  const lastOk = t(circuit.last_success_at);
  const lastFail = t(circuit.last_failure_at);
  const errorIsCurrent = !!circuit.last_error_message && lastFail > lastOk;
  const quiet = circuit.state === 'closed' && (!lastOk || ((now || Date.now()) - lastOk) > QUIET_HOURS * 3600 * 1000);
  return { errorIsCurrent, quiet, resolvedProblemAt: !errorIsCurrent && lastFail ? circuit.last_failure_at : null };
}

const quietConfig = {
  label: 'No recent activity',
  icon: ShieldQuestion,
  color: 'text-amber-600 dark:text-amber-400',
  bg: 'bg-amber-50 dark:bg-amber-950/30',
  border: 'border-amber-200 dark:border-amber-800',
  badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
};

function CircuitCard({ circuit, onReset }) {
  const view = circuitView(circuit);
  const config = view.quiet || view.errorIsCurrent && circuit.state === 'closed'
    ? quietConfig
    : (stateConfig[circuit.state] || stateConfig.closed);
  const StateIcon = config.icon;
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      await onReset(circuit.service);
      toast.success(`Circuit breaker for "${circuit.service}" reset`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const d = new Date(isoStr);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className={cn('transition-all', config.border)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', config.bg)}>
              <StateIcon className={cn('h-5 w-5', config.color)} />
            </div>
            <div>
              <CardTitle className="text-base capitalize">{circuit.service.replace(/_/g, ' ')}</CardTitle>
              <Badge className={cn('mt-1 text-xs font-medium', config.badge)} variant="outline">
                {config.label}
              </Badge>
            </div>
          </div>
          {circuit.state !== 'closed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={resetting}
            >
              {resetting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Failures in a row</p>
            <p className="font-medium">{circuit.failure_count ?? 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Times paused</p>
            <p className="font-medium">{circuit.total_trips ?? 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Last worked</p>
            <p className="font-medium">{formatTime(circuit.last_success_at)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Last problem</p>
            <p className="font-medium">{formatTime(circuit.last_failure_at)}</p>
          </div>
        </div>
        {view.errorIsCurrent && (
          <div className="mt-3 p-2 rounded bg-red-50 dark:bg-red-950/30 text-xs text-red-700 dark:text-red-300 truncate"
            title={circuit.last_error_message}>
            Failing now: {circuit.last_error_message}
          </div>
        )}
        {view.resolvedProblemAt && (
          <p className="mt-3 text-xs text-muted-foreground" title={`${circuit.last_error_type || ''}: ${circuit.last_error_message || ''}`}>
            Last problem on {formatTime(view.resolvedProblemAt)}, working normally since.
          </p>
        )}
        {circuit.state === 'open' && circuit.backoff_until && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Backoff until {formatTime(circuit.backoff_until)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JobCard({ job, onTrigger }) {
  const [triggering, setTriggering] = useState(false);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await onTrigger(job.id);
      toast.success(`Job "${job.name}" triggered`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTriggering(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return 'Not scheduled';
    const d = new Date(isoStr);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate">{job.name}</p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
          <Clock className="h-3 w-3" />
          Next: {formatTime(job.next_run)}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleTrigger}
        disabled={triggering}
      >
        {triggering ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Play className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}

export function SystemHealthPage() {
  const [circuits, setCircuits] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [circuitsRes, schedulerRes, feedsRes] = await Promise.all([
        schedulerAPI.getCircuits(),
        schedulerAPI.getStatus(),
        bankConsentsAPI.get().catch(() => null),
      ]);
      setCircuits(circuitsRes.data.circuits || []);
      setScheduler(schedulerRes.data);
      setFeeds(feedsRes ? feedsRes.data : null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleReset = async (service) => {
    await schedulerAPI.resetCircuit(service);
    await fetchData();
  };

  const handleTrigger = async (jobId) => {
    await schedulerAPI.triggerJob(jobId);
    // Brief delay to let the job start
    setTimeout(fetchData, 2000);
  };

  const openCircuits = circuits.filter((c) => c.state === 'open');
  const halfOpenCircuits = circuits.filter((c) => c.state === 'half_open');
  const closedCircuits = circuits.filter((c) => c.state === 'closed');
  const healthyCircuits = closedCircuits.filter((c) => { const v = circuitView(c); return !v.quiet && !v.errorIsCurrent; });
  const warnCircuits = closedCircuits.filter((c) => { const v = circuitView(c); return v.quiet || v.errorIsCurrent; });
  const trippedCount = openCircuits.length + halfOpenCircuits.length + warnCircuits.length;
  const feedRows = (feeds && feeds.rows) || [];
  const feedsNeedingAction = feedRows.filter((r) => r.state !== 'healthy' && r.state !== 'expiring_soon');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">System Health</h1>
            <p className="text-muted-foreground mt-1">
              Are the app's connections and bank feeds working right now
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Overall status */}
        <Card className={cn(trippedCount + feedsNeedingAction.length > 0
          ? 'border-amber-200 dark:border-amber-800' : 'border-green-200 dark:border-green-800')}>
          <CardContent className="pt-6 flex items-start gap-3">
            {trippedCount + feedsNeedingAction.length > 0
              ? <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              : <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />}
            <div className="text-sm">
              {trippedCount + feedsNeedingAction.length === 0 && <p className="font-medium">Everything is working.</p>}
              {trippedCount > 0 && (
                <p className="font-medium">{trippedCount} app connection{trippedCount === 1 ? '' : 's'} need{trippedCount === 1 ? 's' : ''} a look.</p>
              )}
              {feedsNeedingAction.length > 0 && (
                <p className="font-medium">
                  {feedsNeedingAction.length} of {feedRows.length} bank feeds are not sending data, so figures for those accounts are incomplete.
                  Reconnect them in Integrations.
                </p>
              )}
              {trippedCount === 0 && feedsNeedingAction.length > 0 && (
                <p className="text-muted-foreground mt-0.5">The app's own connections are working.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/30">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{healthyCircuits.length} of {circuits.length}</p>
                  <p className="text-xs text-muted-foreground">App connections working</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', feedsNeedingAction.length ? 'bg-amber-50 dark:bg-amber-950/30' : 'bg-green-50 dark:bg-green-950/30')}>
                  <Landmark className={cn('h-5 w-5', feedsNeedingAction.length ? 'text-amber-600' : 'text-green-600')} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{feedRows.length - feedsNeedingAction.length} of {feedRows.length}</p>
                  <p className="text-xs text-muted-foreground">Bank feeds sending data</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                  <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{scheduler?.jobs?.length ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Automatic jobs scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bank feeds */}
        {feedRows.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Bank feeds
            </h2>
            <Card>
              <CardContent className="pt-4">
                {[...feedsNeedingAction, ...feedRows.filter((r) => r.state === 'expiring_soon'), ...feedRows.filter((r) => r.state === 'healthy')].map((r) => {
                  const ok = r.state === 'healthy';
                  const label = {
                    healthy: 'Working',
                    reconsent_required: 'Reconnect needed',
                    expiring_soon: 'Expires soon',
                    expired: 'Expired, reconnect',
                    no_data: 'No new data',
                    disconnected: 'Disconnected',
                    credentials: 'Login details needed',
                  }[r.state] || r.state;
                  return (
                    <div key={r.connection_id || r.label} className="flex items-center justify-between gap-4 py-2 border-b border-border/50 last:border-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{r.label}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {r.source_label}{r.last_synced ? `, last data ${new Date(r.last_synced).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : ', never synced'}
                        </p>
                      </div>
                      <Badge variant="outline" className={cn('text-xs whitespace-nowrap',
                        ok ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300')}>
                        {label}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Circuit Breakers */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            App connections
          </h2>
          {circuits.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                <p>No connection data yet. It appears after each service is first used.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Show tripped circuits first */}
              {[...openCircuits, ...halfOpenCircuits, ...warnCircuits, ...healthyCircuits].map((circuit) => (
                <CircuitCard
                  key={circuit.service}
                  circuit={circuit}
                  onReset={handleReset}
                />
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Scheduler Jobs */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Automatic jobs
            {scheduler?.running && (
              <Badge variant="outline" className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                Running
              </Badge>
            )}
          </h2>
          {scheduler?.jobs?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {scheduler.jobs.map((job) => (
                <JobCard key={job.id} job={job} onTrigger={handleTrigger} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                {/* scheduler.running is false during the boot window before
                    set_scheduler() runs -- distinct from a scheduler that's
                    up but genuinely has zero jobs (routes/scheduler.py). */}
                <p>{scheduler && !scheduler.running ? 'Scheduler starting…' : 'No scheduler jobs found.'}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
